import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LogOut, Loader2, TrendingUp, TrendingDown, Users, DollarSign, 
  CheckCircle, XCircle, Clock, ArrowRight, AlertCircle, FileText, Download, Eye, Upload, Trash2, Target, Pencil
} from 'lucide-react';
import TradingDayStats from '@/components/TradingDayStats';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import PartnerApprovalModal from '@/components/PartnerApprovalModal';
import EditPartnerModal from '@/components/EditPartnerModal';
import PerformanceFeesTab from '@/components/PerformanceFeesTab';
import PaperTradingManager from '@/components/PaperTradingManager';
import StrategySignalsTab from '@/components/StrategySignalsTab';
import IntradayChart from '@/components/IntradayChart';
import CapitalRequestApprovalModal from '@/components/CapitalRequestApprovalModal';
import { computeTWR, parsePerformanceHistory } from '@/utils/twrCalculation';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
interface TradingSignal {
  id: string;
  trade_date: string;
  stock_symbol: string;
  signal_type: string;
  confidence_score: number;
  notes: string | null;
  is_executed: boolean;
}

interface OpenPosition {
  id: string;
  stock_symbol: string;
  trade_type: string;
  entry_date: string;
  entry_price: number;
  status: string;
  option_type: string | null;
  expiration_date: string | null;
  strike_price: number | null;
  quantity: number | null;
}

interface TradeTarget {
  id: string;
  symbol: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  account_value: number;
  high_water_mark: number;
  total_fees: number;
}

interface OnboardingDocument {
  name: string;
  path: string;
  uploadedAt: string;
}

interface PartnerAccount {
  id: string;
  user_id: string;
  current_value: number;
  total_invested: number;
  ytd_return_pct: number;
  last_updated: string;
  onboarding_status: string;
  display_name: string | null;
  display_email: string | null;
  phone: string | null;
  secondary_email: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  performance_fee_pct: number;
  high_water_mark: number;
  total_fees_paid: number;
  fee_history: unknown;
  performance_history: unknown;
  onboarding_documents: unknown;
  k1_documents: unknown;
  k1_request_status: string | null;
  notes: string | null;
  capital_request_type: string | null;
  capital_request_amount: number | null;
  capital_request_status: string | null;
  capital_request_date: string | null;
  profiles: { full_name: string; email: string } | null;
}

interface K1Document {
  name: string;
  path: string;
  uploadedAt: string;
  taxYear: string;
}

// Helper function to parse onboarding documents
const parseOnboardingDocuments = (docs: unknown): OnboardingDocument[] => {
  if (!docs || !Array.isArray(docs)) return [];
  return docs.filter((doc): doc is OnboardingDocument => 
    typeof doc === 'object' && doc !== null && 'name' in doc && 'path' in doc
  );
};

// Helper function to parse K1 documents
const parseK1Documents = (docs: unknown): K1Document[] => {
  if (!docs || !Array.isArray(docs)) return [];
  return docs.filter((doc): doc is K1Document => 
    typeof doc === 'object' && doc !== null && 'name' in doc && 'path' in doc
  );
};

interface PendingRequest {
  id: string;
  email: string;
  full_name: string;
  message: string | null;
  requested_at: string;
  status: string;
}

interface TradeExecution {
  id: string;
  stock_symbol: string;
  trade_type: string;
  entry_date: string;
  entry_price: number;
  exit_date: string | null;
  exit_price: number | null;
  result_pct: number | null;
  status: string;
  option_type: string | null;
  expiration_date: string | null;
  strike_price: number | null;
  quantity: number | null;
  close_sale_total: number | null;
  aum_at_trade: number | null;
  fees_collected: number | null;
}

// Upcoming Friday (today if Friday), local date as YYYY-MM-DD
const getUpcomingFriday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ManagerDashboard = () => {
  const { user, role, isLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [signals, setSignals] = useState<TradingSignal[]>([]);
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [targets, setTargets] = useState<TradeTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [historyFilter, setHistoryFilter] = useState<string>('ALL');
  const [partnerAccounts, setPartnerAccounts] = useState<PartnerAccount[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [tradeHistory, setTradeHistory] = useState<TradeExecution[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Form states for new trade
  const [newTradeSymbol, setNewTradeSymbol] = useState('');
  const [newTradeOptionType, setNewTradeOptionType] = useState<'call' | 'put'>('call');
  const [newTradeExpiration, setNewTradeExpiration] = useState(getUpcomingFriday);
  const [newTradeStrike, setNewTradeStrike] = useState('');
  const [newTradePurchasePrice, setNewTradePurchasePrice] = useState('');
  const [newTradeQuantity, setNewTradeQuantity] = useState('1');
  
  // Form states for closing trade
  const [closeSaleTotal, setCloseSaleTotal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Approval modal state
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<PendingRequest | null>(null);
  
  // K1 upload state
  const [uploadingK1ForPartner, setUploadingK1ForPartner] = useState<string | null>(null);
  const [k1TaxYear, setK1TaxYear] = useState('');
  
  // Partner removal state
  const [partnerToRemove, setPartnerToRemove] = useState<PartnerAccount | null>(null);
  const [isRemovingPartner, setIsRemovingPartner] = useState(false);
  
  // Partner edit state
  const [partnerToEdit, setPartnerToEdit] = useState<PartnerAccount | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Capital request approval state
  const [capitalRequestPartner, setCapitalRequestPartner] = useState<PartnerAccount | null>(null);
  const [isCapitalRequestModalOpen, setIsCapitalRequestModalOpen] = useState(false);

  // The position of the currently selected target (if any)
  const openPosition = useMemo(
    () => openPositions.find(p => p.stock_symbol === selectedTarget) || null,
    [openPositions, selectedTarget]
  );

  // Redirect if not logged in or not a manager
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/manager-login');
      } else if (role && role !== 'manager') {
        navigate('/manager-login');
      }
    }
  }, [user, role, isLoading, navigate]);

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      if (!user || role !== 'manager') return;

      try {
        // Fetch today's signals
        const today = new Date().toISOString().split('T')[0];
        const { data: signalsData } = await supabase
          .from('manager_trading_data')
          .select('*')
          .eq('trade_date', today)
          .order('created_at', { ascending: false });

        if (signalsData) setSignals(signalsData);

        // Fetch trade targets
        const { data: targetsData } = await supabase
          .from('trade_targets')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (targetsData) {
          setTargets(targetsData as TradeTarget[]);
          setSelectedTarget(prev => prev || targetsData[0]?.symbol || '');
        }

        // Fetch all open positions (one per target)
        const { data: positionData } = await supabase
          .from('trade_executions')
          .select('*')
          .eq('status', 'open')
          .order('entry_date', { ascending: false });

        if (positionData) setOpenPositions(positionData as OpenPosition[]);

        // Fetch partner accounts with profiles
        const { data: accountsData } = await supabase
          .from('partner_accounts')
          .select(`
            *,
            profiles:user_id (full_name, email)
          `)
          .order('current_value', { ascending: false });

        if (accountsData) {
          setPartnerAccounts(accountsData as PartnerAccount[]);
        }

        // Fetch pending requests
        const { data: requestsData } = await supabase
          .from('pending_partner_signups')
          .select('*')
          .eq('status', 'pending')
          .order('requested_at', { ascending: false });

        if (requestsData) setPendingRequests(requestsData);

        // Fetch trade history
        const { data: historyData } = await supabase
          .from('trade_executions')
          .select('*')
          .eq('status', 'closed')
          .order('exit_date', { ascending: false })
          .limit(200);

        if (historyData) setTradeHistory(historyData);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (user && role === 'manager') {
      fetchData();
    }
  }, [user, role]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/manager-login');
  };

  const handleOpenApprovalModal = (request: PendingRequest) => {
    setSelectedRequest(request);
    setIsApprovalModalOpen(true);
  };

  const handleApprovalComplete = () => {
    // Refresh the pending requests list
    const refreshRequests = async () => {
      const { data: requestsData } = await supabase
        .from('pending_partner_signups')
        .select('*')
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (requestsData) setPendingRequests(requestsData);

      // Also refresh partner accounts
      const { data: accountsData } = await supabase
        .from('partner_accounts')
        .select(`
          *,
          profiles:user_id (full_name, email)
        `)
        .order('current_value', { ascending: false });

      if (accountsData) {
        setPartnerAccounts(accountsData as PartnerAccount[]);
      }
    };
    refreshRequests();
  };

  const handleDenyRequest = async (request: PendingRequest) => {
    try {
      const response = await fetch(
        `https://qjgfpmpruszbgvggqdmg.supabase.co/functions/v1/process-partner-approval`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: request.email,
            action: 'deny',
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to deny request');
      }

      toast({
        title: "Request Denied",
        description: `Denied partner request from ${request.full_name}.`,
      });

      // Refresh the list
      handleApprovalComplete();
    } catch (error: any) {
      console.error('Error denying request:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to deny the request.",
        variant: "destructive",
      });
    }
  };

  const handleOpenNewTrade = async () => {
    const symbol = (newTradeSymbol || selectedTarget).toUpperCase();

    if (!symbol || !newTradeExpiration || !newTradeStrike || !newTradePurchasePrice || !newTradeQuantity) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all trade details.",
        variant: "destructive",
      });
      return;
    }

    if (openPositions.some(p => p.stock_symbol === symbol)) {
      toast({
        title: "Position Already Open",
        description: `Close the open ${symbol} position before opening another one for this target.`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const purchasePrice = parseFloat(newTradePurchasePrice);
      const quantity = parseInt(newTradeQuantity);
      const strikePrice = parseFloat(newTradeStrike);
      
      // Calculate current AUM from active partners
      const activePartners = partnerAccounts.filter(a => a.onboarding_status === 'active');
      const currentAUM = activePartners.reduce((sum, acc) => sum + acc.current_value, 0);

      // Create trade execution record with AUM at trade time
      const { error: tradeError } = await supabase
        .from('trade_executions')
        .insert({
          stock_symbol: symbol,
          trade_type: newTradeOptionType,
          option_type: newTradeOptionType,
          expiration_date: newTradeExpiration,
          strike_price: strikePrice,
          entry_date: new Date().toISOString().split('T')[0],
          entry_price: purchasePrice,
          quantity: quantity,
          status: 'open',
          executed_by: user?.id,
          aum_at_trade: currentAUM,
        });

      if (tradeError) throw tradeError;

      toast({
        title: "Trade Opened",
        description: `Opened ${symbol} ${newTradeOptionType.toUpperCase()} $${strikePrice} exp ${newTradeExpiration} @ $${purchasePrice} × ${quantity}`,
      });

      // Reset form
      setNewTradeSymbol('');
      setNewTradeOptionType('call');
      setNewTradeExpiration(getUpcomingFriday());
      setNewTradeStrike('');
      setNewTradePurchasePrice('');
      setNewTradeQuantity('1');

      // Refresh data
      window.location.reload();

    } catch (error: any) {
      console.error('Error opening trade:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to open trade.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePosition = async () => {
    if (!openPosition) return;

    if (!closeSaleTotal || isNaN(parseFloat(closeSaleTotal))) {
      toast({
        title: "Invalid Close Sale Total",
        description: "Please enter the total sale amount from closing the position.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const closeSaleTotalNum = parseFloat(closeSaleTotal);
      const quantity = openPosition.quantity || 1;
      // Options contracts are for 100 shares, so multiply by 100
      const purchaseCost = openPosition.entry_price * quantity * 100;
      const dollarReturn = closeSaleTotalNum - purchaseCost;
      
      // Calculate result percentage based on the total investment (cost basis)
      const resultPct = (dollarReturn / purchaseCost) * 100;
      
      // Calculate exit price per contract for records (divide back by 100 for per-share premium)
      const exitPricePerContract = closeSaleTotalNum / quantity / 100;

      // Update trade execution with all the details
      const { error: tradeError } = await supabase
        .from('trade_executions')
        .update({
          exit_date: new Date().toISOString().split('T')[0],
          exit_price: exitPricePerContract,
          close_sale_total: closeSaleTotalNum,
          result_pct: resultPct,
          status: 'closed',
          updated_at: new Date().toISOString(),
        })
        .eq('id', openPosition.id);

      if (tradeError) throw tradeError;

      // Get only ACTIVE partner accounts for proportional distribution
      const activePartners = partnerAccounts.filter(a => a.onboarding_status === 'active');

      // Calculate total AUM of active partners for proportional distribution
      const totalActiveAUM = activePartners.reduce((sum, acc) => sum + acc.current_value, 0);

      // ---- Per-target high water mark & performance fee -------------------
      const target = targets.find(t => t.symbol === openPosition.stock_symbol);
      const feePct = activePartners[0]?.performance_fee_pct ?? 20;
      let totalFeesCollected = 0;

      // Fees are ACCRUED here (tracked as earned) but NOT deducted from value/HWM.
      // They only reduce AUM/HWM when the manager logs a withdrawal in the Fees tab.
      if (target) {
        const targetValue = (target.account_value || 0) + dollarReturn;
        let newTargetHWM = target.high_water_mark || 0;

        if (targetValue > newTargetHWM) {
          totalFeesCollected = (targetValue - newTargetHWM) * (feePct / 100);
          newTargetHWM = targetValue;
        }

        const { error: targetError } = await supabase
          .from('trade_targets')
          .update({
            account_value: targetValue,
            high_water_mark: newTargetHWM,
            total_fees: (target.total_fees || 0) + totalFeesCollected,
          })
          .eq('id', target.id);

        if (targetError) console.error('Error updating target:', targetError);
      }

      // Full gross return goes to partners; accrued fees stay in the fund until withdrawn
      const netReturnToPartners = dollarReturn;

      // Distribute returns proportionally to active partners based on their current value
      for (const account of activePartners) {
        // Calculate this partner's share of the total AUM
        const partnerShare = totalActiveAUM > 0 ? account.current_value / totalActiveAUM : 0;
        const partnerFee = totalFeesCollected * partnerShare;
        const newValue = account.current_value + netReturnToPartners * partnerShare;

        const currentFeeHistory = Array.isArray(account.fee_history) ? account.fee_history : [];
        const updatedFeeHistory = [...currentFeeHistory];
        const newHWM = Math.max(account.high_water_mark || 0, newValue);

        if (partnerFee > 0) {
          updatedFeeHistory.push({
            date: new Date().toISOString().split('T')[0],
            amount: partnerFee,
            partnerName: account.profiles?.full_name || 'Unknown',
            target: openPosition.stock_symbol,
            accountValueBefore: account.current_value,
            accountValueAfter: newValue,
            hwmBefore: account.high_water_mark,
            hwmAfter: newHWM,
          });
        }

        // Update performance history with new data point
        const existingPerformanceHistory = Array.isArray(account.performance_history) 
          ? account.performance_history 
          : [];
        const updatedPerformanceHistory = [
          ...existingPerformanceHistory,
          {
            date: new Date().toISOString().split('T')[0],
            value: newValue,
            change_pct: account.current_value ? ((newValue - account.current_value) / account.current_value) * 100 : 0,
            hwm: newHWM,
          }
        ];

        const ytdReturnPct = computeTWR(
          updatedPerformanceHistory as Array<{ date: string; value: number; event?: string; event_amount?: number }>,
          newValue,
          account.total_invested
        );

        const { error: accountError } = await supabase
          .from('partner_accounts')
          .update({
            current_value: newValue,
            high_water_mark: newHWM,
            total_fees_paid: (account.total_fees_paid || 0) + partnerFee,
            fee_history: updatedFeeHistory,
            performance_history: updatedPerformanceHistory,
            ytd_return_pct: ytdReturnPct,
            last_updated: new Date().toISOString(),
          })
          .eq('id', account.id);

        if (accountError) {
          console.error('Error updating account:', account.id, accountError);
        }
      }

      const feeMessage = totalFeesCollected > 0 
        ? ` Performance fees collected: $${totalFeesCollected.toFixed(2)}.`
        : '';

      // Attribute the fees generated by this close to the trade (per-target fee tracking)
      await supabase
        .from('trade_executions')
        .update({ fees_collected: totalFeesCollected })
        .eq('id', openPosition.id);

      toast({
        title: "Position Closed",
        description: `Closed ${openPosition.stock_symbol} ${openPosition.option_type?.toUpperCase() || ''} with $${dollarReturn.toFixed(2)} return (${resultPct >= 0 ? '+' : ''}${resultPct.toFixed(2)}%). Updated ${activePartners.length} partner accounts.${feeMessage}`,
      });

      // Reset form and refresh
      setCloseSaleTotal('');
      window.location.reload();

    } catch (error: any) {
      console.error('Error closing position:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to close position.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleK1Upload = async (partnerId: string, userId: string, file: File) => {
    if (!k1TaxYear) {
      toast({
        title: "Tax Year Required",
        description: "Please enter the tax year for this K-1 document.",
        variant: "destructive",
      });
      return;
    }

    setUploadingK1ForPartner(partnerId);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}_K1_${k1TaxYear}_${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('k1-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get existing K1 documents for this partner
      const partner = partnerAccounts.find(p => p.id === partnerId);
      const existingDocs = parseK1Documents(partner?.k1_documents);

      const newDoc: K1Document = {
        name: file.name,
        path: filePath,
        uploadedAt: new Date().toISOString(),
        taxYear: k1TaxYear,
      };

      // Update partner account with new K1 document - convert to plain object for JSON
      const updatedDocs = [...existingDocs, newDoc].map(doc => ({
        name: doc.name,
        path: doc.path,
        uploadedAt: doc.uploadedAt,
        taxYear: doc.taxYear,
      }));

      const { error: updateError } = await supabase
        .from('partner_accounts')
        .update({
          k1_documents: updatedDocs,
          k1_request_status: null, // Clear any pending request
          last_updated: new Date().toISOString(),
        })
        .eq('id', partnerId);

      if (updateError) throw updateError;

      toast({
        title: "K-1 Uploaded",
        description: `Successfully uploaded K-1 for tax year ${k1TaxYear}.`,
      });

      // Refresh partner accounts
      handleApprovalComplete();
      setK1TaxYear('');
    } catch (error: any) {
      console.error('Error uploading K1:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload K-1 document.",
        variant: "destructive",
      });
    } finally {
      setUploadingK1ForPartner(null);
    }
  };

  const handleRemovePartner = async () => {
    if (!partnerToRemove) return;
    
    setIsRemovingPartner(true);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await supabase.functions.invoke('remove-partner', {
        body: {
          partnerId: partnerToRemove.id,
          userId: partnerToRemove.user_id,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to remove partner");
      }

      toast({
        title: "Partner Removed",
        description: `${partnerToRemove.profiles?.full_name || 'Partner'} has been removed from the fund.`,
      });

      // Refresh partner accounts
      handleApprovalComplete();
      setPartnerToRemove(null);
      
    } catch (error: any) {
      console.error('Error removing partner:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove partner.",
        variant: "destructive",
      });
    } finally {
      setIsRemovingPartner(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const activePartners = partnerAccounts.filter(a => a.onboarding_status === 'active');
  const totalAUM = activePartners.reduce((sum, acc) => sum + acc.current_value, 0);
  const totalHWM = activePartners.reduce((sum, acc) => sum + (acc.high_water_mark || 0), 0);
  const fundHWMDeficit = Math.max(0, totalHWM - totalAUM);

  // Calculate initial total investment across all active partners
  const initialTotalInvestment = useMemo(() => {
    return activePartners.reduce((sum, acc) => sum + acc.total_invested, 0);
  }, [activePartners]);

  // Aggregate fund performance history across all partners by date
  const fundPerformanceData = useMemo(() => {
    const dateMap = new Map<string, { totalValue: number; hwm: number }>();
    
    // Get all performance entries from all active partners
    activePartners.forEach(partner => {
      const history = Array.isArray(partner.performance_history) 
        ? partner.performance_history as Array<{ date: string; value: number; hwm?: number }>
        : [];
      
      history.forEach(entry => {
        const existing = dateMap.get(entry.date) || { totalValue: 0, hwm: 0 };
        // Use the HWM recorded at that point in time, falling back to current HWM
        const entryHwm = entry.hwm !== undefined ? entry.hwm : partner.high_water_mark;
        dateMap.set(entry.date, {
          totalValue: existing.totalValue + entry.value,
          hwm: existing.hwm + (entryHwm || 0),
        });
      });
    });

    // Convert to sorted array
    const sortedData = Array.from(dateMap.entries())
      .map(([date, data]) => ({
        date,
        value: data.totalValue,
        hwm: data.hwm,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // If we have data but no starting point matches initial investment, add it
    if (sortedData.length > 0 && initialTotalInvestment > 0) {
      const firstDate = new Date(sortedData[0].date);
      // Add an initial data point the day before the first entry if it doesn't start at initial investment
      if (sortedData[0].value !== initialTotalInvestment) {
        const initialDate = new Date(firstDate);
        initialDate.setDate(initialDate.getDate() - 1);
        sortedData.unshift({
          date: initialDate.toISOString().split('T')[0],
          value: initialTotalInvestment,
          hwm: initialTotalInvestment,
        });
      }
    } else if (sortedData.length === 0 && initialTotalInvestment > 0) {
      // If no performance data yet but we have partners, show initial investment
      const today = new Date().toISOString().split('T')[0];
      sortedData.push({
        date: today,
        value: initialTotalInvestment,
        hwm: initialTotalInvestment,
      });
    }

    return sortedData;
  }, [activePartners, initialTotalInvestment]);

  // ---- Multi-target analytics -------------------------------------------
  const tradePL = (t: TradeExecution) => {
    const qty = t.quantity || 1;
    const cost = t.entry_price * qty * 100;
    const sale = t.close_sale_total || (t.exit_price ? t.exit_price * qty * 100 : 0);
    return { cost, sale, pl: sale - cost };
  };

  const totalOpenCost = useMemo(
    () => openPositions.reduce((sum, p) => sum + (p.entry_price || 0) * (p.quantity || 1) * 100, 0),
    [openPositions]
  );

  const targetStats = useMemo(() => {
    return targets.map(target => {
      const trades = tradeHistory.filter(t => t.stock_symbol === target.symbol);
      const netPL = trades.reduce((sum, t) => sum + tradePL(t).pl, 0);
      const profits = trades.reduce((sum, t) => Math.max(0, tradePL(t).pl) + sum, 0);
      const deficits = trades.reduce((sum, t) => Math.min(0, tradePL(t).pl) + sum, 0);
      const fees = trades.reduce((sum, t) => sum + (t.fees_collected || 0), 0);
      const wins = trades.filter(t => tradePL(t).pl > 0).length;
      const avgReturn = trades.length
        ? trades.reduce((sum, t) => sum + (t.result_pct || 0), 0) / trades.length
        : 0;
      return {
        ...target,
        trades: trades.length,
        netPL,
        profits,
        deficits,
        fees: target.total_fees || fees,
        accountValue: target.account_value || 0,
        hwm: target.high_water_mark || 0,
        hwmDeficit: Math.max(0, (target.high_water_mark || 0) - (target.account_value || 0)),
        winRate: trades.length ? (wins / trades.length) * 100 : 0,
        avgReturn,
        position: openPositions.find(p => p.stock_symbol === target.symbol) || null,
      };
    });
  }, [targets, tradeHistory, openPositions]);

  const cumulativeStats = useMemo(() => ({
    trades: targetStats.reduce((s, t) => s + t.trades, 0),
    netPL: targetStats.reduce((s, t) => s + t.netPL, 0),
    profits: targetStats.reduce((s, t) => s + t.profits, 0),
    deficits: targetStats.reduce((s, t) => s + t.deficits, 0),
    fees: targetStats.reduce((s, t) => s + t.fees, 0),
    accountValue: targetStats.reduce((s, t) => s + t.accountValue, 0),
    hwm: targetStats.reduce((s, t) => s + t.hwm, 0),
  }), [targetStats]);

  const filteredTradeHistory = useMemo(
    () => historyFilter === 'ALL' ? tradeHistory : tradeHistory.filter(t => t.stock_symbol === historyFilter),
    [tradeHistory, historyFilter]
  );

  if (isLoading || !user) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow manager-dashboard">
        <div className="mx-auto w-full max-w-[1920px] px-3 sm:px-4 lg:px-6 py-4 sm:py-5 lg:py-6">
          {/* Header */}
          <div className="flex justify-between items-center gap-3 mb-4 sm:mb-5 lg:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-delphi-light">Manager Dashboard</h1>
              <p className="hidden sm:block text-sm text-delphi-light/60 mt-1">Trade Management & Partner Overview</p>
            </div>
            <Button 
              variant="outline" 
              className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
              onClick={handleSignOut}
            >
                <LogOut size={16} className="sm:mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>

          {isLoadingData ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
            </div>
          ) : (
            <Tabs defaultValue="portfolio" className="space-y-3 sm:space-y-4">
              <TabsList className="w-full bg-gradient-to-r from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5 grid grid-cols-3 h-auto gap-1 p-1 sm:p-1.5">
                <TabsTrigger value="portfolio" className="px-1.5 sm:px-3 text-xs sm:text-sm whitespace-normal leading-tight">Portfolio Overview</TabsTrigger>
                <TabsTrigger value="activity" className="px-1.5 sm:px-3 text-xs sm:text-sm whitespace-normal leading-tight">Target Activity</TabsTrigger>
                <TabsTrigger value="actions" className="px-1.5 sm:px-3 text-xs sm:text-sm whitespace-normal leading-tight">Manager Actions</TabsTrigger>
              </TabsList>

              <TabsContent value="portfolio" className="mt-0">
              {/* Top Stats Row - 3 Enhanced Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-4">
                {/* AUM Card with % at Risk */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="p-4 lg:p-5 flex flex-col items-center justify-center text-center min-h-[118px]">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign size={20} className="text-delphi-red" />
                      <span className="text-sm font-medium text-delphi-light/70 uppercase tracking-wide">Total AUM</span>
                    </div>
                    <p className="text-2xl xl:text-3xl font-bold text-delphi-light mb-2">{formatCurrency(totalAUM)}</p>
                    <div className="w-full pt-2 border-t border-delphi-gray/20 space-y-0.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-delphi-light/60">Active Partners</span>
                        <span className="text-delphi-light font-medium">{activePartners.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-delphi-light/60">% at Risk</span>
                        <span className={`font-semibold ${totalOpenCost > 0 ? 'text-yellow-400' : 'text-delphi-light/50'}`}>
                          {totalOpenCost > 0 && totalAUM > 0 
                            ? `${(totalOpenCost / totalAUM * 100).toFixed(2)}%`
                            : '0%'}
                        </span>
                      </div>
                      {totalOpenCost > 0 && totalAUM > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-delphi-light/60">Open Positions Value</span>
                          <span className="text-delphi-light font-medium">
                            {formatCurrency(totalOpenCost)}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* HWM Card */}
                <Card className={`bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border shadow-lg ${
                  fundHWMDeficit > 0 
                    ? 'border-red-500/50 shadow-red-500/10' 
                    : 'border-green-500/30 shadow-green-500/5'
                }`}>
                  <CardContent className="p-4 lg:p-5 flex flex-col items-center justify-center text-center min-h-[118px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Target size={20} className={fundHWMDeficit > 0 ? 'text-red-400' : 'text-green-400'} />
                      <span className="text-sm font-medium text-delphi-light/70 uppercase tracking-wide">HWM Status</span>
                    </div>
                    <p className={`text-2xl xl:text-3xl font-bold mb-2 ${fundHWMDeficit > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {fundHWMDeficit > 0 ? `-${formatCurrency(fundHWMDeficit)}` : 'At HWM'}
                    </p>
                    <div className="w-full pt-2 border-t border-delphi-gray/20 space-y-0.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-delphi-light/60">Total HWM</span>
                        <span className="text-delphi-light font-medium">{formatCurrency(totalHWM)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-delphi-light/60">Status</span>
                        <span className={`font-medium ${fundHWMDeficit > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {fundHWMDeficit > 0 ? 'Recovery Needed' : 'Fee Eligible'}
                        </span>
                      </div>
                      {fundHWMDeficit > 0 && totalAUM > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-delphi-light/60">Recovery %</span>
                          <span className="text-yellow-400 font-medium">
                            {((fundHWMDeficit / totalAUM) * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Open Positions Across Targets */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="p-4 lg:p-5 flex flex-col items-center justify-center text-center min-h-[118px]">
                    <div className="flex items-center gap-2 mb-2">
                      {openPositions.length > 0 ? (
                        <TrendingUp size={20} className="text-green-500" />
                      ) : (
                        <Clock size={20} className="text-yellow-500" />
                      )}
                      <span className="text-sm font-medium text-delphi-light/70 uppercase tracking-wide">Open Positions</span>
                    </div>
                    <p className="text-2xl xl:text-3xl font-bold text-delphi-light mb-2">
                      {openPositions.length} / {targets.length}
                    </p>
                    <div className="w-full pt-2 border-t border-delphi-gray/20 space-y-0.5">
                      {targetStats.map(t => (
                        <div key={t.id} className="flex justify-between text-sm">
                          <span className="text-delphi-light/60">{t.symbol}</span>
                          <span className={`font-medium ${t.position ? 'text-green-400' : 'text-delphi-light/50'}`}>
                            {t.position
                              ? `${t.position.option_type?.toUpperCase() || ''} $${t.position.strike_price} × ${t.position.quantity || 1}`
                              : 'Flat'}
                          </span>
                        </div>
                      ))}
                      {targets.length === 0 && (
                        <p className="text-sm text-delphi-light/50">No trade targets configured</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Per-Target Performance (individual + cumulative) */}
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 lg:gap-4 mb-4">
                {targetStats.map(t => (
                  <Card key={t.id} className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-gray/30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-lg font-bold text-delphi-light">{t.symbol}</p>
                          <p className="text-xs text-delphi-light/50">{t.name}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {t.position && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-delphi-gray/20 text-delphi-light">
                              {t.position.option_type?.toUpperCase() || ''} ${t.position.strike_price ?? '---'}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.position ? 'bg-green-500/20 text-green-400' : 'bg-delphi-gray/20 text-delphi-light/50'}`}>
                            {t.position ? 'Open' : 'Flat'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.9fr)_minmax(210px,1.1fr)] gap-3">
                      <div>
                      <p className="text-2xl font-bold mb-1 text-delphi-light">{formatCurrency(t.accountValue)}</p>
                      <p className={`text-sm font-medium mb-2 ${t.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {t.netPL >= 0 ? '+' : ''}{formatCurrency(t.netPL)} net P/L
                      </p>
                      <div className="space-y-0.5 text-xs xl:text-sm border-t border-delphi-gray/20 pt-2">
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">High Water Mark</span>
                          <span className="text-delphi-light font-medium">{formatCurrency(t.hwm)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">HWM Status</span>
                          <span className={`font-medium ${t.hwmDeficit > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {t.hwmDeficit > 0 ? `-${formatCurrency(t.hwmDeficit)}` : 'At HWM'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">Profits</span>
                          <span className="text-green-400 font-medium">+{formatCurrency(t.profits)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">Deficits</span>
                          <span className="text-red-400 font-medium">{formatCurrency(t.deficits)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">Fees Generated</span>
                          <span className="text-delphi-light font-medium">{formatCurrency(t.fees)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-delphi-light/60">Trades / Win Rate</span>
                          <span className="text-delphi-light font-medium">{t.trades} / {t.winRate.toFixed(0)}%</span>
                        </div>
                      </div>
                      </div>
                      <IntradayChart symbol={t.symbol} compact />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/40">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-lg font-bold text-delphi-light">Cumulative</p>
                        <p className="text-xs text-delphi-light/50">All targets combined</p>
                      </div>
                      <Target size={18} className="text-delphi-red" />
                    </div>
                    <p className={`text-2xl font-bold mb-2 ${cumulativeStats.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {cumulativeStats.netPL >= 0 ? '+' : ''}{formatCurrency(cumulativeStats.netPL)}
                    </p>
                    <div className="space-y-0.5 text-sm border-t border-delphi-gray/20 pt-2">
                      <div className="flex justify-between">
                        <span className="text-delphi-light/60">Profits</span>
                        <span className="text-green-400 font-medium">+{formatCurrency(cumulativeStats.profits)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-delphi-light/60">Deficits</span>
                        <span className="text-red-400 font-medium">{formatCurrency(cumulativeStats.deficits)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-delphi-light/60">Fees Generated</span>
                        <span className="text-delphi-light font-medium">{formatCurrency(cumulativeStats.fees)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-delphi-light/60">Total Trades</span>
                        <span className="text-delphi-light font-medium">{cumulativeStats.trades}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              </TabsContent>

              <TabsContent value="activity" className="mt-0">
              {/* Target Selector */}
              {targets.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                  <span className="text-sm text-delphi-light/60 mr-1">Active Target:</span>
                  {targets.map(t => (
                    <Button
                      key={t.id}
                      size="sm"
                      variant={selectedTarget === t.symbol ? 'default' : 'outline'}
                      className={selectedTarget === t.symbol
                        ? 'bg-delphi-red hover:bg-red-600'
                        : 'border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                      onClick={() => { setSelectedTarget(t.symbol); setCloseSaleTotal(''); }}
                    >
                      {t.symbol}
                      {openPositions.some(p => p.stock_symbol === t.symbol) && (
                        <span className="ml-2 w-2 h-2 rounded-full bg-green-400 inline-block" />
                      )}
                    </Button>
                  ))}
                </div>
              )}

              {/* Bloomberg TV + Intraday Chart + Current Position Row */}
              <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)_360px] 2xl:grid-cols-[320px_minmax(0,1fr)_420px] gap-3 lg:gap-4 mb-4 items-stretch">
                {/* Current Position Card - Narrower */}
                <div className="order-2 xl:order-1 min-w-0">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5 h-full">
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-xl text-delphi-light flex items-center gap-2">
                        {openPosition ? (
                          <TrendingUp size={20} className="text-green-500" />
                        ) : (
                          <Clock size={20} className="text-yellow-500" />
                        )}
                        {selectedTarget ? `${selectedTarget} Position` : 'Current Position'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      {!openPosition ? (
                        <p className="text-delphi-light/50 text-center py-4">No open {selectedTarget} position. Open a trade from the "New Trade" tab below.</p>
                      ) : (
                        <div className="space-y-3">
                          {/* Position Details - Compact Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Symbol</p>
                              <p className="text-lg font-bold text-delphi-light">{openPosition.stock_symbol}</p>
                            </div>
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Type</p>
                              <p className={`text-lg font-bold uppercase ${openPosition.option_type === 'call' ? 'text-green-400' : 'text-red-400'}`}>
                                {openPosition.option_type || openPosition.trade_type}
                              </p>
                            </div>
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Strike</p>
                              <p className="text-lg font-bold text-delphi-light">${openPosition.strike_price || '---'}</p>
                            </div>
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Expiration</p>
                              <p className="text-lg font-bold text-delphi-light">
                                {openPosition.expiration_date ? new Date(openPosition.expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '---'}
                              </p>
                            </div>
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Entry</p>
                              <p className="text-lg font-bold text-delphi-light">${openPosition.entry_price}</p>
                            </div>
                            <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
                              <p className="text-delphi-light/50 text-xs mb-0.5">Qty</p>
                              <p className="text-lg font-bold text-delphi-light">{openPosition.quantity || 1}</p>
                            </div>
                          </div>

                          {/* Total Value - Compact */}
                          <div className="p-3 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                            <p className="text-delphi-light/50 text-xs">Total Position Value</p>
                            <p className="text-xl font-bold text-delphi-light">
                              ${((openPosition.entry_price || 0) * (openPosition.quantity || 1) * 100).toFixed(2)}
                            </p>
                            <p className="text-delphi-light/40 text-xs mt-1">Entry: {openPosition.entry_date}</p>
                          </div>

                          {/* Close Position - Compact */}
                          <div className="border-t border-delphi-gray/20 pt-3">
                            <Label className="text-delphi-light/70 text-xs mb-1 block">
                              Close Sale Total ($)
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Sale amount"
                                value={closeSaleTotal}
                                onChange={(e) => setCloseSaleTotal(e.target.value)}
                                className="bg-delphi-dark text-delphi-light border-delphi-gray/40 text-sm"
                              />
                              <Button
                                className="bg-delphi-red hover:bg-red-600 px-3"
                                onClick={handleClosePosition}
                                disabled={isSubmitting || !closeSaleTotal}
                                size="sm"
                              >
                                {isSubmitting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                            {closeSaleTotal && !isNaN(parseFloat(closeSaleTotal)) && (
                              <div className="mt-2 p-2 rounded-lg border text-center" style={{
                                background: (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                  ? 'rgba(34, 197, 94, 0.1)' 
                                  : 'rgba(239, 68, 68, 0.1)',
                                borderColor: (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                  ? 'rgba(34, 197, 94, 0.3)' 
                                  : 'rgba(239, 68, 68, 0.3)'
                              }}>
                                <p className={`text-sm font-bold ${
                                  (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                    ? 'text-green-500' 
                                    : 'text-red-500'
                                }`}>
                                  {(parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 ? '+' : ''}
                                  ${(parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)).toFixed(2)} 
                                  ({(((parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) / (openPosition.entry_price * (openPosition.quantity || 1) * 100)) * 100).toFixed(1)}%)
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Intraday Chart - Center */}
                <div className="order-3 xl:order-2 min-w-0">
                  {openPosition || selectedTarget ? (
                    <IntradayChart symbol={openPosition ? openPosition.stock_symbol : selectedTarget} />
                  ) : (
                    <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5 h-full min-h-[320px] flex items-center justify-center">
                      <CardContent className="text-center py-12">
                        <TrendingUp size={48} className="mx-auto text-delphi-light/20 mb-4" />
                        <p className="text-delphi-light/50">Open a position to view intraday chart</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Trading Day Stats with Bloomberg TV */}
                <div className="order-1 xl:order-3 min-w-0">
                  <TradingDayStats />
                </div>
              </div>

          {/* Open New Position */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-xl text-delphi-light">Open New Position</CardTitle>
                    <CardDescription>Select a target and enter the options contract details</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-2">
                    {openPosition ? (
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                        <p className="text-yellow-400 font-medium">{openPosition.stock_symbol} Position Currently Open</p>
                        <p className="text-delphi-light/70 text-sm mt-1">
                          Close the open {openPosition.stock_symbol} {openPosition.option_type?.toUpperCase()} position before opening another trade on this target.
                          {targets.some(t => !openPositions.some(p => p.stock_symbol === t.symbol)) && ' You can still open a trade on another target by switching the active target above.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
                          <div>
                            <Label className="text-delphi-light/70 text-sm mb-2 block">Target</Label>
                            <div className="flex gap-2 flex-wrap">
                              {targets.map(t => {
                                const taken = openPositions.some(p => p.stock_symbol === t.symbol);
                                return (
                                  <Button
                                    key={t.id}
                                    type="button"
                                    disabled={taken}
                                    variant={selectedTarget === t.symbol ? 'default' : 'outline'}
                                    className={selectedTarget === t.symbol
                                      ? 'bg-delphi-red hover:bg-red-600'
                                      : 'border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                                    onClick={() => setSelectedTarget(t.symbol)}
                                  >
                                    {t.symbol}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <Label className="text-delphi-light/70 text-sm mb-2 block">Option Type</Label>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant={newTradeOptionType === 'call' ? 'default' : 'outline'}
                                className={newTradeOptionType === 'call' 
                                  ? 'flex-1 bg-green-600 hover:bg-green-700' 
                                  : 'flex-1 border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                                onClick={() => setNewTradeOptionType('call')}
                              >
                                CALL
                              </Button>
                            <Button
                              type="button"
                              variant={newTradeOptionType === 'put' ? 'default' : 'outline'}
                              className={newTradeOptionType === 'put' 
                                ? 'flex-1 bg-red-600 hover:bg-red-700' 
                                : 'flex-1 border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                              onClick={() => setNewTradeOptionType('put')}
                            >
                              PUT
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-delphi-light/70 text-sm mb-2 block">Expiration Date</Label>
                          <Input
                            type="date"
                            value={newTradeExpiration}
                            onChange={(e) => setNewTradeExpiration(e.target.value)}
                            className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                          />
                        </div>
                        <div>
                          <Label className="text-delphi-light/70 text-sm mb-2 block">Strike Price ($)</Label>
                          <Input
                            type="number"
                            placeholder="e.g. 580"
                            value={newTradeStrike}
                            onChange={(e) => setNewTradeStrike(e.target.value)}
                            className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                          />
                        </div>
                        <div>
                          <Label className="text-delphi-light/70 text-sm mb-2 block">Purchase Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="e.g. 4.25"
                            value={newTradePurchasePrice}
                            onChange={(e) => setNewTradePurchasePrice(e.target.value)}
                            className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                          />
                        </div>
                        <div>
                          <Label className="text-delphi-light/70 text-sm mb-2 block">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={newTradeQuantity}
                            onChange={(e) => setNewTradeQuantity(e.target.value)}
                            className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                          />
                        </div>
                      </div>

                      {/* Trade Summary Preview */}
                      {selectedTarget && newTradePurchasePrice && newTradeQuantity && (
                        <div className="p-4 bg-delphi-red/10 border border-delphi-red/30 rounded-lg">
                          <p className="text-delphi-light/70 text-sm mb-2">Trade Summary</p>
                          <p className="text-delphi-light font-medium">
                            {selectedTarget} {newTradeOptionType.toUpperCase()} ${newTradeStrike || '---'} 
                            {newTradeExpiration ? ` exp ${newTradeExpiration}` : ''} @ ${newTradePurchasePrice} × {newTradeQuantity}
                          </p>
                          <p className="text-delphi-light/50 text-sm mt-1">
                            Total Cost: ${(parseFloat(newTradePurchasePrice || '0') * parseInt(newTradeQuantity || '0') * 100).toFixed(2)}
                            <span className="text-delphi-light/40 ml-2">(${newTradePurchasePrice} × {newTradeQuantity} contracts × 100 shares)</span>
                          </p>
                        </div>
                      )}

                      <Button
                        className="w-full bg-delphi-red hover:bg-red-600 py-5 text-base"
                        onClick={handleOpenNewTrade}
                        disabled={isSubmitting || !selectedTarget || !newTradeExpiration || !newTradeStrike || !newTradePurchasePrice}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Opening Position...
                          </>
                        ) : (
                          <>
                            <TrendingUp className="w-5 h-5 mr-2" />
                            Open Position
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              </TabsContent>

              <TabsContent value="actions" className="mt-0 dashboard-actions">
              {/* Manager Actions Panel (Tabs) */}
              <div className="mb-4">
                <div className="flex flex-col lg:flex-row lg:items-start gap-2 lg:gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-delphi-light lg:pt-2 flex-shrink-0">Manager Actions</h2>
                <Tabs defaultValue="strategy-signals" className="space-y-3 min-w-0 flex-1">
                  <TabsList className="w-full bg-gradient-to-r from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5 flex flex-wrap h-auto gap-1 p-1.5">
                    <TabsTrigger value="strategy-signals" className="text-xs sm:text-sm">Signals</TabsTrigger>
                    <TabsTrigger value="fund-performance" className="text-xs sm:text-sm">Fund</TabsTrigger>
                    <TabsTrigger value="partners" className="text-xs sm:text-sm">Partners</TabsTrigger>
                    <TabsTrigger value="fees" className="text-xs sm:text-sm">Fees</TabsTrigger>
                    <TabsTrigger value="k1docs" className="text-xs sm:text-sm hidden sm:flex">K-1</TabsTrigger>
                    <TabsTrigger value="paper-trading" className="text-xs sm:text-sm">Paper</TabsTrigger>
                    <TabsTrigger value="requests" className="text-xs sm:text-sm">
                      <span className="hidden sm:inline">Requests</span>
                      <span className="sm:hidden">Req</span>
                      {pendingRequests.length > 0 && <span className="ml-1 text-yellow-400">({pendingRequests.length})</span>}
                    </TabsTrigger>
                    <TabsTrigger value="activation" className="text-xs sm:text-sm hidden sm:flex">
                      Activation ({partnerAccounts.filter(a => a.onboarding_status === 'onboarding_complete').length})
                    </TabsTrigger>
                    <TabsTrigger value="history" className="text-xs sm:text-sm">History</TabsTrigger>
                  </TabsList>

                {/* Current Position Tab */}
                <TabsContent value="position">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Current Open Position</CardTitle>
                      <CardDescription>Close position to update all partner accounts proportionally</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {!openPosition ? (
                        <p className="text-delphi-light/50 text-center py-8">No open position. Open a trade from the "Today's Signals" tab.</p>
                      ) : (
                        <div className="space-y-6">
                          {/* Position Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            <div>
                              <p className="text-delphi-light/50 text-sm">Symbol</p>
                              <p className="text-xl font-bold text-delphi-light">{openPosition.stock_symbol}</p>
                            </div>
                            <div>
                              <p className="text-delphi-light/50 text-sm">Type</p>
                              <p className="text-xl font-bold text-delphi-light uppercase">
                                {openPosition.option_type || openPosition.trade_type}
                              </p>
                            </div>
                            <div>
                              <p className="text-delphi-light/50 text-sm">Strike</p>
                              <p className="text-xl font-bold text-delphi-light">
                                ${openPosition.strike_price || '---'}
                              </p>
                            </div>
                            <div>
                              <p className="text-delphi-light/50 text-sm">Expiration</p>
                              <p className="text-xl font-bold text-delphi-light">
                                {openPosition.expiration_date || '---'}
                              </p>
                            </div>
                            <div>
                              <p className="text-delphi-light/50 text-sm">Entry Price</p>
                              <p className="text-xl font-bold text-delphi-light">${openPosition.entry_price}</p>
                            </div>
                            <div>
                              <p className="text-delphi-light/50 text-sm">Quantity</p>
                              <p className="text-xl font-bold text-delphi-light">{openPosition.quantity || 1}</p>
                            </div>
                          </div>

                          {/* Purchase Cost Summary */}
                          <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-delphi-light/50 text-sm">Entry Date</p>
                                <p className="text-delphi-light font-medium">{openPosition.entry_date}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-delphi-light/50 text-sm">Total Purchase Cost</p>
                                <p className="text-xl font-bold text-delphi-light">
                                  ${((openPosition.entry_price || 0) * (openPosition.quantity || 1) * 100).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Close Position Form */}
                          <div className="border-t border-delphi-gray/20 pt-6">
                            <h4 className="text-lg font-medium text-delphi-light mb-4">Close Position</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-delphi-light/70 text-sm mb-2 block">
                                  Total Sale Amount ($)
                                  <span className="text-delphi-light/40 ml-2 text-xs">(Enter total received from sale)</span>
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="e.g. 5250.00"
                                  value={closeSaleTotal}
                                  onChange={(e) => setCloseSaleTotal(e.target.value)}
                                  className="bg-delphi-dark text-delphi-light border-delphi-gray/40 text-lg py-6"
                                />
                              </div>

                              {/* Real-time P/L Preview */}
                              {closeSaleTotal && !isNaN(parseFloat(closeSaleTotal)) && (
                                <div className="flex flex-col justify-center">
                                  <div className="p-4 rounded-lg border" style={{
                                    background: (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                      ? 'rgba(34, 197, 94, 0.1)' 
                                      : 'rgba(239, 68, 68, 0.1)',
                                    borderColor: (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                      ? 'rgba(34, 197, 94, 0.3)' 
                                      : 'rgba(239, 68, 68, 0.3)'
                                  }}>
                                    <p className="text-delphi-light/50 text-sm mb-1">Dollar Return</p>
                                    <p className={`text-2xl font-bold ${
                                      (parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 
                                        ? 'text-green-500' 
                                        : 'text-red-500'
                                    }`}>
                                      {(parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) >= 0 ? '+' : ''}
                                      ${(parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)).toFixed(2)}
                                    </p>
                                    <p className="text-delphi-light/50 text-sm mt-1">
                                      ({(((parseFloat(closeSaleTotal) - (openPosition.entry_price * (openPosition.quantity || 1) * 100)) / (openPosition.entry_price * (openPosition.quantity || 1) * 100)) * 100).toFixed(2)}%)
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            <Button
                              className="w-full mt-6 bg-delphi-red hover:bg-red-600 py-6 text-lg"
                              onClick={handleClosePosition}
                              disabled={isSubmitting || !closeSaleTotal}
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                  Closing & Updating Accounts...
                                </>
                              ) : (
                                <>
                                  <TrendingDown className="w-5 h-5 mr-2" />
                                  Close Position & Distribute Returns
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Strategy Signals Tab */}
                <TabsContent value="strategy-signals">
                  <StrategySignalsTab />
                </TabsContent>

                {/* Fund Performance Tab */}
                <TabsContent value="fund-performance">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Fund Performance Over Time</CardTitle>
                      <CardDescription>Total AUM and performance history across all active partners</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {fundPerformanceData.length === 0 ? (
                        <div className="h-80 flex items-center justify-center text-delphi-light/50">
                          <p>Fund performance data will appear here after trades are closed.</p>
                        </div>
                      ) : (
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={fundPerformanceData}
                              margin={{ top: 5, right: 30, bottom: 5, left: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                              <XAxis 
                                dataKey="date" 
                                stroke="#666" 
                                tick={{ fill: '#999' }}
                                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              />
                              <YAxis
                                stroke="#666"
                                tick={{ fill: '#999' }}
                                tickFormatter={(value) => formatCurrency(value)}
                              />
                              <Tooltip
                                contentStyle={{ background: '#1a1a1a', border: '1px solid #333' }}
                                labelStyle={{ color: '#fff' }}
                                formatter={(value: number, name: string) => [
                                  formatCurrency(value), 
                                  name === 'value' ? 'Total AUM' : 'High Water Mark'
                                ]}
                                labelFormatter={(label) => new Date(label).toLocaleDateString()}
                              />
                              <Legend />
                              <Line 
                                type="monotone" 
                                dataKey="value" 
                                name="Total AUM"
                                stroke="#ea384c" 
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4, stroke: "#ea384c", strokeWidth: 2, fill: "#fff" }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="hwm" 
                                name="High Water Mark"
                                stroke="#22c55e" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                activeDot={{ r: 4, stroke: "#22c55e", strokeWidth: 2, fill: "#fff" }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Partner Accounts Tab */}
                <TabsContent value="partners">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Partner Accounts</CardTitle>
                      <CardDescription>Overview of all partner investment accounts</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {partnerAccounts.length === 0 ? (
                        <p className="text-delphi-light/50 text-center py-8">No partner accounts yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-delphi-red/20">
                                <th className="text-left py-3 px-4 text-delphi-light/70 font-medium">Partner</th>
                                <th className="text-left py-3 px-4 text-delphi-light/70 font-medium">Status</th>
                                <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Current Value</th>
                                <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Invested</th>
                                <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Perf. Fee</th>
                                <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">YTD Return</th>
                                <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {partnerAccounts.map((account) => (
                                <tr key={account.id} className="border-b border-delphi-gray/10 hover:bg-delphi-red/5 transition-colors">
                                  <td className="py-3 px-4">
                                    <p className="text-delphi-light font-medium">{account.display_name || account.profiles?.full_name || 'Unknown'}</p>
                                    <p className="text-delphi-light/50 text-sm">{account.display_email || account.profiles?.email}</p>
                                    {account.capital_request_status === 'pending' && (
                                      <div className="flex flex-col gap-1 mt-2">
                                        <span className="inline-flex items-center text-yellow-500 text-xs">
                                          <Clock size={12} className="mr-1" />
                                          {account.capital_request_type === 'deposit' ? 'Deposit' : 'Withdrawal'} Request: ${account.capital_request_amount?.toLocaleString()}
                                        </span>
                                        <div className="flex gap-2">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 px-3 text-xs bg-green-600/20 text-green-400 border-green-500/40 hover:bg-green-600/30 hover:border-green-500/60"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setCapitalRequestPartner(account);
                                              setIsCapitalRequestModalOpen(true);
                                            }}
                                          >
                                            <CheckCircle size={12} className="mr-1" />
                                            Review Request
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      account.onboarding_status === 'active' 
                                        ? 'bg-green-500/20 text-green-400' 
                                        : account.onboarding_status === 'onboarding_complete'
                                        ? 'bg-yellow-500/20 text-yellow-400'
                                        : 'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {account.onboarding_status === 'active' ? 'Active' : 
                                       account.onboarding_status === 'onboarding_complete' ? 'Awaiting Activation' : 
                                       'Onboarding'}
                                    </span>
                                  </td>
                                  <td className="text-right py-3 px-4 text-delphi-light font-medium">
                                    {formatCurrency(account.current_value)}
                                  </td>
                                  <td className="text-right py-3 px-4 text-delphi-light/70">
                                    {formatCurrency(account.total_invested)}
                                  </td>
                                  <td className="text-right py-3 px-4 text-delphi-red font-medium">
                                    {account.performance_fee_pct}%
                                  </td>
                                  {(() => {
                                    const twrPct = computeTWR(
                                      parsePerformanceHistory(account.performance_history),
                                      account.current_value,
                                      account.total_invested
                                    );
                                    return (
                                      <td className={`text-right py-3 px-4 font-medium ${
                                        twrPct >= 0 ? 'text-green-500' : 'text-red-500'
                                      }`}>
                                        {twrPct >= 0 ? '+' : ''}{twrPct.toFixed(2)}%
                                      </td>
                                    );
                                  })()}
                                  <td className="text-right py-3 px-4">
                                    <div className="flex items-center justify-end gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-delphi-light border-delphi-gray/30 hover:bg-delphi-gray/10 hover:border-delphi-gray/50"
                                        onClick={() => {
                                          setPartnerToEdit(account);
                                          setIsEditModalOpen(true);
                                        }}
                                      >
                                        <Pencil size={14} className="mr-1" />
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-400 border-red-400/30 hover:bg-red-400/10 hover:border-red-400/50"
                                        onClick={() => setPartnerToRemove(account)}
                                      >
                                        <Trash2 size={14} className="mr-1" />
                                        Remove
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Performance Fees Tab */}
                <TabsContent value="fees">
                  <PerformanceFeesTab 
                    partnerAccounts={partnerAccounts} 
                    targets={targets}
                    formatCurrency={formatCurrency} 
                    onWithdrawalRecorded={async () => {
                      const [{ data: partnersData }, { data: targetsData }] = await Promise.all([
                        supabase.from('partner_accounts').select(`*, profiles:user_id (full_name, email)`).order('created_at', { ascending: false }),
                        supabase.from('trade_targets').select('*').eq('is_active', true).order('sort_order', { ascending: true }),
                      ]);
                      if (partnersData) setPartnerAccounts(partnersData as PartnerAccount[]);
                      if (targetsData) setTargets(targetsData as TradeTarget[]);
                    }}
                  />
                </TabsContent>

                {/* K1 Tax Documents Tab */}
                <TabsContent value="k1docs">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light flex items-center">
                        <FileText size={20} className="mr-2 text-delphi-red" />
                        Tax Documents (Schedule K-1)
                      </CardTitle>
                      <CardDescription>Upload and manage K-1 tax documents for active partners</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {partnerAccounts.filter(a => a.onboarding_status === 'active').length === 0 ? (
                        <p className="text-delphi-light/50 text-center py-8">No active partners yet.</p>
                      ) : (
                        <div className="space-y-6">
                          {partnerAccounts.filter(a => a.onboarding_status === 'active').map((account) => {
                            const k1Docs = parseK1Documents(account.k1_documents);
                            return (
                              <div key={account.id} className="p-4 border border-delphi-gray/20 rounded-lg">
                                <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="text-lg font-medium text-delphi-light">{account.display_name || account.profiles?.full_name || 'Unknown'}</p>
                                    <p className="text-delphi-light/50">{account.display_email || account.profiles?.email}</p>
                                    {account.k1_request_status === 'pending' && (
                                      <span className="inline-flex items-center text-yellow-500 text-sm mt-1">
                                        <Clock size={14} className="mr-1" />
                                        K-1 Requested
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* K1 Upload Form */}
                                  <div className="flex items-end gap-3">
                                    <div className="space-y-1">
                                      <Label className="text-delphi-light/70 text-xs">Tax Year</Label>
                                      <Input
                                        type="text"
                                        placeholder="2025"
                                        value={uploadingK1ForPartner === account.id ? k1TaxYear : ''}
                                        onChange={(e) => {
                                          setUploadingK1ForPartner(account.id);
                                          setK1TaxYear(e.target.value);
                                        }}
                                        className="w-24 bg-delphi-dark text-delphi-light border-delphi-gray/40"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-delphi-light/70 text-xs">Upload K-1</Label>
                                      <div className="relative">
                                        <input
                                          type="file"
                                          id={`k1-upload-${account.id}`}
                                          accept=".pdf,.doc,.docx"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                              handleK1Upload(account.id, account.user_id, file);
                                            }
                                            e.target.value = '';
                                          }}
                                          className="hidden"
                                          disabled={uploadingK1ForPartner === account.id && !k1TaxYear}
                                        />
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                          onClick={() => document.getElementById(`k1-upload-${account.id}`)?.click()}
                                          disabled={uploadingK1ForPartner === account.id && uploadingK1ForPartner !== null}
                                        >
                                          {uploadingK1ForPartner === account.id ? (
                                            <Loader2 size={14} className="mr-1 animate-spin" />
                                          ) : (
                                            <Upload size={14} className="mr-1" />
                                          )}
                                          Upload K-1
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Existing K1 Documents */}
                                {k1Docs.length > 0 ? (
                                  <div className="mt-4 pt-4 border-t border-delphi-gray/20">
                                    <p className="text-sm font-medium text-delphi-light/80 mb-3">
                                      Uploaded K-1 Documents ({k1Docs.length})
                                    </p>
                                    <div className="space-y-2">
                                      {k1Docs.map((doc, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between p-3 bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg"
                                        >
                                          <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-delphi-red" />
                                            <div>
                                              <span className="text-delphi-light text-sm">{doc.name}</span>
                                              <p className="text-delphi-light/40 text-xs">
                                                Tax Year: {doc.taxYear} • Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                              onClick={async () => {
                                                const { data } = await supabase.storage
                                                  .from('k1-documents')
                                                  .createSignedUrl(doc.path, 60);
                                                if (data?.signedUrl) {
                                                  window.open(data.signedUrl, '_blank');
                                                }
                                              }}
                                            >
                                              <Eye size={14} className="mr-1" />
                                              View
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                              onClick={async () => {
                                                const { data } = await supabase.storage
                                                  .from('k1-documents')
                                                  .createSignedUrl(doc.path, 60);
                                                if (data?.signedUrl) {
                                                  const link = document.createElement('a');
                                                  link.href = data.signedUrl;
                                                  link.download = doc.name;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                }
                                              }}
                                            >
                                              <Download size={14} className="mr-1" />
                                              Download
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-4 pt-4 border-t border-delphi-gray/20">
                                    <p className="text-sm text-delphi-light/50 italic">
                                      No K-1 documents uploaded yet for this partner.
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Pending Requests Tab */}
                <TabsContent value="requests">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Pending Partner Requests</CardTitle>
                      <CardDescription>Review and approve new partner applications</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {pendingRequests.length === 0 ? (
                        <p className="text-delphi-light/50 text-center py-8">No pending requests.</p>
                      ) : (
                        <div className="space-y-4">
                          {pendingRequests.map((request) => (
                            <div key={request.id} className="p-4 border border-delphi-gray/20 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-lg font-medium text-delphi-light">{request.full_name}</p>
                                  <p className="text-delphi-light/50">{request.email}</p>
                                  {request.message && (
                                    <p className="text-delphi-light/70 text-sm mt-2 italic">"{request.message}"</p>
                                  )}
                                  <p className="text-delphi-light/40 text-xs mt-2">
                                    Requested: {new Date(request.requested_at).toLocaleString()}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-400 border-red-400/50 hover:bg-red-400/10"
                                    onClick={() => handleDenyRequest(request)}
                                  >
                                    <XCircle size={16} className="mr-1" />
                                    Deny
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleOpenApprovalModal(request)}
                                  >
                                    <CheckCircle size={16} className="mr-1" />
                                    Approve
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Awaiting Activation Tab */}
                <TabsContent value="activation">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Partners Awaiting Activation</CardTitle>
                      <CardDescription>Partners who have completed onboarding and are ready for final activation</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {partnerAccounts.filter(a => a.onboarding_status === 'onboarding_complete').length === 0 ? (
                        <p className="text-delphi-light/50 text-center py-8">No partners awaiting activation.</p>
                      ) : (
                        <div className="space-y-6">
                          {partnerAccounts.filter(a => a.onboarding_status === 'onboarding_complete').map((account) => {
                            const documents = parseOnboardingDocuments(account.onboarding_documents);
                            return (
                              <div key={account.id} className="p-4 border border-delphi-gray/20 rounded-lg">
                                <div className="flex justify-between items-start mb-4">
                                  <div>
                                    <p className="text-lg font-medium text-delphi-light">{account.profiles?.full_name || 'Unknown'}</p>
                                    <p className="text-delphi-light/50">{account.profiles?.email}</p>
                                    <div className="mt-2 text-sm text-delphi-light/70">
                                      <p>Investment: {formatCurrency(account.total_invested)}</p>
                                      <p>Performance Fee: {account.performance_fee_pct}%</p>
                                      {account.phone && <p>Phone: {account.phone}</p>}
                                      {account.city && account.state && <p>Location: {account.city}, {account.state}</p>}
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={async () => {
                                      // Set HWM to current_value when activating to ensure fee calculations start from here
                                      const { error } = await supabase
                                        .from('partner_accounts')
                                        .update({ 
                                          onboarding_status: 'active',
                                          inception_date: new Date().toISOString().split('T')[0],
                                          high_water_mark: account.current_value,
                                          last_updated: new Date().toISOString()
                                        })
                                        .eq('id', account.id);
                                      if (!error) {
                                        toast({ title: "Partner Activated", description: `${account.profiles?.full_name}'s account is now active.` });
                                        handleApprovalComplete();
                                      }
                                    }}
                                  >
                                    <CheckCircle size={16} className="mr-1" />
                                    Activate Account
                                  </Button>
                                </div>
                                
                                {/* Uploaded Documents Section */}
                                {documents.length > 0 && (
                                  <div className="mt-4 pt-4 border-t border-delphi-gray/20">
                                    <p className="text-sm font-medium text-delphi-light/80 mb-3 flex items-center">
                                      <FileText size={16} className="mr-2" />
                                      Uploaded Documents ({documents.length})
                                    </p>
                                    <div className="space-y-2">
                                      {documents.map((doc, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between p-3 bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg"
                                        >
                                          <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-delphi-red" />
                                            <div>
                                              <span className="text-delphi-light text-sm">{doc.name}</span>
                                              {doc.uploadedAt && (
                                                <p className="text-delphi-light/40 text-xs">
                                                  Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                              onClick={async () => {
                                                const { data } = await supabase.storage
                                                  .from('partner-documents')
                                                  .createSignedUrl(doc.path, 60); // 60 second expiry
                                                if (data?.signedUrl) {
                                                  window.open(data.signedUrl, '_blank');
                                                } else {
                                                  toast({
                                                    title: "Error",
                                                    description: "Failed to generate document link.",
                                                    variant: "destructive",
                                                  });
                                                }
                                              }}
                                            >
                                              <Eye size={14} className="mr-1" />
                                              View
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                              onClick={async () => {
                                                const { data } = await supabase.storage
                                                  .from('partner-documents')
                                                  .createSignedUrl(doc.path, 60);
                                                if (data?.signedUrl) {
                                                  const link = document.createElement('a');
                                                  link.href = data.signedUrl;
                                                  link.download = doc.name;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                } else {
                                                  toast({
                                                    title: "Error",
                                                    description: "Failed to download document.",
                                                    variant: "destructive",
                                                  });
                                                }
                                              }}
                                            >
                                              <Download size={14} className="mr-1" />
                                              Download
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {documents.length === 0 && (
                                  <div className="mt-4 pt-4 border-t border-delphi-gray/20">
                                    <p className="text-sm text-delphi-light/50 italic">
                                      No documents uploaded by this partner.
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Trade History Tab */}
                <TabsContent value="history">
                  <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                    <CardHeader>
                      <CardTitle className="text-xl text-delphi-light">Trade History & Analytics</CardTitle>
                      <CardDescription>Closed options positions with performance metrics</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Per-target breakdown */}
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-delphi-gray/20">
                              <th className="text-left py-2 px-2 text-delphi-light/70 font-medium">Target</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Value</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">HWM</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Trades</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Profits</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Deficits</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Net P/L</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Fees</th>
                              <th className="text-right py-2 px-2 text-delphi-light/70 font-medium">Win Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {targetStats.map(t => (
                              <tr key={t.id} className="border-b border-delphi-gray/10">
<td className="py-2 px-2 text-delphi-light font-medium">{t.symbol}<span className="text-delphi-light/40 ml-2 text-xs">{t.name}</span></td>
                                <td className="text-right py-2 px-2 text-delphi-light">{formatCurrency(t.accountValue)}</td>
                                <td className={`text-right py-2 px-2 ${t.hwmDeficit > 0 ? 'text-red-400' : 'text-delphi-light/70'}`}>{formatCurrency(t.hwm)}</td>
                                <td className="text-right py-2 px-2 text-delphi-light/70">{t.trades}</td>
                                <td className="text-right py-2 px-2 text-green-500">+{formatCurrency(t.profits)}</td>
                                <td className="text-right py-2 px-2 text-red-500">{formatCurrency(t.deficits)}</td>
                                <td className={`text-right py-2 px-2 font-medium ${t.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>{t.netPL >= 0 ? '+' : ''}{formatCurrency(t.netPL)}</td>
                                <td className="text-right py-2 px-2 text-delphi-light/70">{formatCurrency(t.fees)}</td>
                                <td className="text-right py-2 px-2 text-delphi-light/70">{t.winRate.toFixed(0)}%</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-delphi-red/30 bg-delphi-red/5">
                              <td className="py-2 px-2 font-medium text-delphi-light">CUMULATIVE</td>
                              <td className="text-right py-2 px-2 text-delphi-light font-medium">{formatCurrency(cumulativeStats.accountValue)}</td>
                              <td className="text-right py-2 px-2 text-delphi-light">{formatCurrency(cumulativeStats.hwm)}</td>
                              <td className="text-right py-2 px-2 text-delphi-light">{cumulativeStats.trades}</td>
                              <td className="text-right py-2 px-2 text-green-500">+{formatCurrency(cumulativeStats.profits)}</td>
                              <td className="text-right py-2 px-2 text-red-500">{formatCurrency(cumulativeStats.deficits)}</td>
                              <td className={`text-right py-2 px-2 font-bold ${cumulativeStats.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>{cumulativeStats.netPL >= 0 ? '+' : ''}{formatCurrency(cumulativeStats.netPL)}</td>
                              <td className="text-right py-2 px-2 text-delphi-light">{formatCurrency(cumulativeStats.fees)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* History filter */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Button
                          size="sm"
                          variant={historyFilter === 'ALL' ? 'default' : 'outline'}
                          className={historyFilter === 'ALL' ? 'bg-delphi-red hover:bg-red-600' : 'border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                          onClick={() => setHistoryFilter('ALL')}
                        >
                          All Targets
                        </Button>
                        {targets.map(t => (
                          <Button
                            key={t.id}
                            size="sm"
                            variant={historyFilter === t.symbol ? 'default' : 'outline'}
                            className={historyFilter === t.symbol ? 'bg-delphi-red hover:bg-red-600' : 'border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20'}
                            onClick={() => setHistoryFilter(t.symbol)}
                          >
                            {t.symbol}
                          </Button>
                        ))}
                      </div>

                      {filteredTradeHistory.length === 0 ? (
                        <p className="text-delphi-light/50 text-center py-8">No closed trades yet{historyFilter !== 'ALL' ? ` for ${historyFilter}` : ''}.</p>
                      ) : (
                        <>
                          {/* Summary Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                              <p className="text-delphi-light/50 text-xs uppercase">Total Trades</p>
                              <p className="text-2xl font-bold text-delphi-light">{filteredTradeHistory.length}</p>
                            </div>
                            <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                              <p className="text-delphi-light/50 text-xs uppercase">Win Rate</p>
                              <p className="text-2xl font-bold text-green-500">
                                {((filteredTradeHistory.filter(t => (t.result_pct || 0) > 0).length / filteredTradeHistory.length) * 100).toFixed(0)}%
                              </p>
                            </div>
                            <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                              <p className="text-delphi-light/50 text-xs uppercase">Total P/L</p>
                              {(() => {
                                const totalPL = filteredTradeHistory.reduce((sum, t) => {
                                  const qty = t.quantity || 1;
                                  const cost = t.entry_price * qty * 100;
                                  const sale = t.close_sale_total || (t.exit_price ? t.exit_price * qty * 100 : 0);
                                  return sum + (sale - cost);
                                }, 0);
                                return (
                                  <p className={`text-2xl font-bold ${totalPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                  </p>
                                );
                              })()}
                            </div>
                            <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                              <p className="text-delphi-light/50 text-xs uppercase">Avg Return</p>
                              {(() => {
                                const avgReturn = filteredTradeHistory.reduce((sum, t) => sum + (t.result_pct || 0), 0) / filteredTradeHistory.length;
                                return (
                                  <p className={`text-2xl font-bold ${avgReturn >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {avgReturn >= 0 ? '+' : ''}{avgReturn.toFixed(1)}%
                                  </p>
                                );
                              })()}
                            </div>
                            <div className="p-4 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg text-center">
                              <p className="text-delphi-light/50 text-xs uppercase">Avg Hold Days</p>
                              <p className="text-2xl font-bold text-delphi-light">
                                {(filteredTradeHistory.reduce((sum, t) => {
                                  if (t.entry_date && t.exit_date) {
                                    const entry = new Date(t.entry_date);
                                    const exit = new Date(t.exit_date);
                                    return sum + Math.ceil((exit.getTime() - entry.getTime()) / (1000 * 60 * 60 * 24));
                                  }
                                  return sum;
                                }, 0) / filteredTradeHistory.length).toFixed(1)}
                              </p>
                            </div>
                          </div>

                          {/* Trade Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-delphi-gray/20">
                                  <th className="text-left py-3 px-2 text-delphi-light/70 font-medium">Contract</th>
                                  <th className="text-center py-3 px-2 text-delphi-light/70 font-medium">Days</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">Cost</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">Sale</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">P/L ($)</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">P/L (%)</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">AUM Risk</th>
                                  <th className="text-right py-3 px-2 text-delphi-light/70 font-medium">AUM Impact</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTradeHistory.map((trade, index) => {
                                  const quantity = trade.quantity || 1;
                                  const purchaseCost = trade.entry_price * quantity * 100;
                                  const saleTotal = trade.close_sale_total || (trade.exit_price ? trade.exit_price * quantity * 100 : 0);
                                  const dollarReturn = saleTotal - purchaseCost;
                                  
                                  // Calculate days held
                                  let daysHeld = 0;
                                  if (trade.entry_date && trade.exit_date) {
                                    const entry = new Date(trade.entry_date);
                                    const exit = new Date(trade.exit_date);
                                    daysHeld = Math.ceil((exit.getTime() - entry.getTime()) / (1000 * 60 * 60 * 24));
                                  }
                                  
                                  // Calculate % of AUM at risk
                                  const aumAtRisk = trade.aum_at_trade ? (purchaseCost / trade.aum_at_trade) * 100 : null;
                                  
                                  // Calculate AUM impact (return as % of AUM)
                                  const aumImpact = trade.aum_at_trade ? (dollarReturn / trade.aum_at_trade) * 100 : null;
                                  
                                  // Calculate cumulative P/L
                                  const cumulativePL = filteredTradeHistory
                                    .slice(0, index + 1)
                                    .reduce((sum, t) => {
                                      const qty = t.quantity || 1;
                                      const cost = t.entry_price * qty * 100;
                                      const sale = t.close_sale_total || (t.exit_price ? t.exit_price * qty * 100 : 0);
                                      return sum + (sale - cost);
                                    }, 0);
                                  
                                  return (
                                    <tr key={trade.id} className="border-b border-delphi-gray/10 hover:bg-delphi-red/5 transition-colors">
                                      {/* Contract Details */}
                                      <td className="py-3 px-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium uppercase ${
                                            trade.option_type === 'call' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                          }`}>
                                            {trade.option_type === 'call' ? 'C' : 'P'}
                                          </span>
                                          <div>
                                            <p className="text-delphi-light font-medium">
                                              {trade.stock_symbol} ${trade.strike_price}
                                            </p>
                                            <p className="text-delphi-light/40 text-xs">
                                              {trade.entry_date} → {trade.exit_date} | {quantity}x
                                            </p>
                                          </div>
                                        </div>
                                      </td>
                                      
                                      {/* Days Held */}
                                      <td className="text-center py-3 px-2 text-delphi-light/70">
                                        {daysHeld}d
                                      </td>
                                      
                                      {/* Cost */}
                                      <td className="text-right py-3 px-2 text-delphi-light/70">
                                        ${purchaseCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                      </td>
                                      
                                      {/* Sale */}
                                      <td className="text-right py-3 px-2 text-delphi-light/70">
                                        ${saleTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                      </td>
                                      
                                      {/* P/L Dollar */}
                                      <td className={`text-right py-3 px-2 font-medium ${
                                        dollarReturn >= 0 ? 'text-green-500' : 'text-red-500'
                                      }`}>
                                        {dollarReturn >= 0 ? '+' : ''}${dollarReturn.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                      </td>
                                      
                                      {/* P/L Percent */}
                                      <td className={`text-right py-3 px-2 font-medium ${
                                        (trade.result_pct || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                                      }`}>
                                        {(trade.result_pct || 0) >= 0 ? '+' : ''}{(trade.result_pct || 0).toFixed(1)}%
                                      </td>
                                      
                                      {/* AUM Risk */}
                                      <td className="text-right py-3 px-2 text-delphi-light/50">
                                        {aumAtRisk !== null ? `${aumAtRisk.toFixed(2)}%` : '-'}
                                      </td>
                                      
                                      {/* AUM Impact */}
                                      <td className={`text-right py-3 px-2 font-medium ${
                                        aumImpact !== null && aumImpact >= 0 ? 'text-green-500' : aumImpact !== null ? 'text-red-500' : 'text-delphi-light/50'
                                      }`}>
                                        {aumImpact !== null ? `${aumImpact >= 0 ? '+' : ''}${aumImpact.toFixed(3)}%` : '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              
                              {/* Table Footer with Totals */}
                              <tfoot>
                                <tr className="border-t-2 border-delphi-red/30 bg-delphi-red/5">
                                  <td colSpan={2} className="py-3 px-2 font-medium text-delphi-light">
                                    TOTALS
                                  </td>
                                  <td className="text-right py-3 px-2 font-medium text-delphi-light">
                                    ${filteredTradeHistory.reduce((sum, t) => sum + (t.entry_price * (t.quantity || 1) * 100), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                  </td>
                                  <td className="text-right py-3 px-2 font-medium text-delphi-light">
                                    ${filteredTradeHistory.reduce((sum, t) => {
                                      const qty = t.quantity || 1;
                                      return sum + (t.close_sale_total || (t.exit_price ? t.exit_price * qty * 100 : 0));
                                    }, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                  </td>
                                  {(() => {
                                    const totalPL = filteredTradeHistory.reduce((sum, t) => {
                                      const qty = t.quantity || 1;
                                      const cost = t.entry_price * qty * 100;
                                      const sale = t.close_sale_total || (t.exit_price ? t.exit_price * qty * 100 : 0);
                                      return sum + (sale - cost);
                                    }, 0);
                                    return (
                                      <td className={`text-right py-3 px-2 font-bold ${totalPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                      </td>
                                    );
                                  })()}
                                  <td colSpan={3}></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Paper Trading Tab */}
                <TabsContent value="paper-trading">
                  <PaperTradingManager />
                </TabsContent>
                </Tabs>
                </div>
              </div>
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Partner Approval Modal */}
        <PartnerApprovalModal
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          request={selectedRequest}
          onApprovalComplete={handleApprovalComplete}
        />

        {/* Partner Removal Confirmation Dialog */}
        <AlertDialog open={!!partnerToRemove} onOpenChange={(open) => !open && setPartnerToRemove(null)}>
          <AlertDialogContent className="bg-delphi-dark border border-delphi-red/30">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-delphi-light">Remove Partner</AlertDialogTitle>
              <AlertDialogDescription className="text-delphi-light/70">
                Are you sure you want to remove <span className="font-medium text-delphi-light">{partnerToRemove?.profiles?.full_name || 'this partner'}</span> from the fund?
                <br /><br />
                This will permanently delete:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Their user account and login credentials</li>
                  <li>Partner account data (${partnerToRemove ? formatCurrency(partnerToRemove.current_value) : '$0'} current value)</li>
                  <li>All uploaded documents (onboarding & K-1)</li>
                </ul>
                <br />
                <span className="text-red-400 font-medium">This action cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-transparent border-delphi-gray/40 text-delphi-light hover:bg-delphi-gray/20"
                disabled={isRemovingPartner}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleRemovePartner}
                disabled={isRemovingPartner}
              >
                {isRemovingPartner ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Partner
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Partner Modal */}
        <EditPartnerModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setPartnerToEdit(null);
          }}
          partner={partnerToEdit}
          onUpdateComplete={() => {
            // Refresh partner accounts data
            const fetchPartners = async () => {
              const { data: partnersData } = await supabase
                .from('partner_accounts')
                .select(`
                  *,
                  profiles:user_id (full_name, email)
                `)
                .order('created_at', { ascending: false });

              if (partnersData) {
                setPartnerAccounts(partnersData as PartnerAccount[]);
              }
            };
            fetchPartners();
          }}
        />

        {/* Capital Request Approval Modal */}
        <CapitalRequestApprovalModal
          isOpen={isCapitalRequestModalOpen}
          onClose={() => {
            setIsCapitalRequestModalOpen(false);
            setCapitalRequestPartner(null);
          }}
          partner={capitalRequestPartner}
          onComplete={handleApprovalComplete}
        />
      </main>
    </PageLayout>
  );
};

export default ManagerDashboard;
